import type * as React from 'react';
import { cn } from '../lib/cn.ts';

export interface NavItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  href?: string;
  active?: boolean;
  badge?: string | number;
  onClick?: () => void;
}

export interface NavGroup {
  id: string;
  label?: string;
  items: NavItem[];
}

export interface SidebarNavProps {
  groups: NavGroup[];
  className?: string;
  'data-testid'?: string;
}

function SidebarNav({ groups, className, 'data-testid': testId }: SidebarNavProps) {
  return (
    <nav
      data-testid={testId ?? 'sidebar-nav'}
      aria-label="Main navigation"
      className={cn('flex flex-col gap-6 px-3 py-4', className)}
    >
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-0.5">
          {group.label && (
            <p
              className={cn(
                'mb-1.5 px-2',
                'font-[var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.14em]',
                'text-[var(--color-rail-muted)]',
              )}
            >
              {group.label}
            </p>
          )}
          {group.items.map((item) => (
            <SidebarNavItem key={item.id} item={item} />
          ))}
        </div>
      ))}
    </nav>
  );
}

interface SidebarNavItemProps {
  item: NavItem;
}

function SidebarNavItem({ item }: SidebarNavItemProps) {
  // Prototype `.navitem`: gap 11px, r-sm radius, 13.5px, rail ink; active = the
  // charcoal rail-active surface (NOT a big pill), hover = the quiet rail wash.
  const sharedClassName = cn(
    'group flex w-full items-center gap-[11px] rounded-md px-2.5 py-2',
    'text-[13.5px] tracking-[-0.01em] transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
    item.active
      ? 'bg-[var(--color-rail-active-bg)] text-[var(--color-rail-active-ink)] font-medium'
      : 'text-[var(--color-rail-ink)] hover:bg-[var(--color-rail-hover)]',
  );

  const innerContent = (
    <>
      {item.icon && (
        <span
          aria-hidden="true"
          className={cn(
            'shrink-0 [&>svg]:size-[17px] [&>svg]:stroke-[1.5]',
            item.active ? 'opacity-100' : 'opacity-[0.82]',
          )}
        >
          {item.icon}
        </span>
      )}
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge !== undefined && (
        <span
          className={cn(
            'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-center',
            'font-[var(--font-mono)] text-[10px] font-medium',
            item.active
              ? 'bg-white/20 text-[var(--color-rail-active-ink)]'
              : 'bg-[var(--color-stone-300)] text-[var(--color-charcoal)]',
          )}
        >
          {item.badge}
        </span>
      )}
    </>
  );

  if (item.href) {
    return (
      <a
        data-testid={`sidebar-nav-item-${item.id}`}
        href={item.href}
        onClick={(e) => {
          // Plain left-click → client-side navigation (SPA). Modifier/middle
          // clicks keep the browser default (open in new tab/window) since the
          // href is a real URL.
          if (
            item.onClick &&
            e.button === 0 &&
            !e.metaKey &&
            !e.ctrlKey &&
            !e.shiftKey &&
            !e.altKey
          ) {
            e.preventDefault();
            item.onClick();
          }
        }}
        aria-current={item.active ? 'page' : undefined}
        className={sharedClassName}
      >
        {innerContent}
      </a>
    );
  }

  return (
    <button
      type="button"
      data-testid={`sidebar-nav-item-${item.id}`}
      onClick={item.onClick}
      aria-current={item.active ? 'page' : undefined}
      className={sharedClassName}
    >
      {innerContent}
    </button>
  );
}

export { SidebarNav };
